import { Lock, Unlock } from "lucide-react";

import { useCandidateCreationGate } from "@/api/validation";

/**
 * States whether a preview candidate could be frozen from this draft right now,
 * and when it could not, why.
 *
 * This is a **status, not a control**. Issue #8 owns the candidate-creation
 * action; #7 owns the gate that decides whether it may act. Rendering the
 * verdict here means the gate is visible and testable before #8 exists, without
 * putting a button on screen that #8 has not built yet.
 *
 * #8 consumes `useCandidateCreationGate` directly and attaches `detail` to its
 * own control as the disabled reason, so the control it does build is disabled
 * with this same reason rather than deriving its own. (`ActionBar` was named
 * here as that host until #516 deleted it as a noncanonical surface; the gate
 * and its `detail` are what the seam actually is.)
 */
export function CandidateGateNotice({ projectId }: { projectId: string }) {
  const gate = useCandidateCreationGate(projectId);

  if (gate.allowed) {
    return (
      <p
        role="status"
        className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
      >
        <Unlock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          A preview candidate can be frozen from draft version{" "}
          <code className="font-mono text-xs">{gate.draftVersion}</code>.
        </span>
      </p>
    );
  }

  return (
    <p
      role="status"
      className="flex items-start gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground"
    >
      <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        <span className="font-medium text-foreground">
          Preview candidate creation is blocked.
        </span>{" "}
        {gate.detail}
      </span>
    </p>
  );
}
